import React, { useState, useEffect } from "react";
import { Link, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react";

// --- Core Imports ---
import { useAuth } from "../../contexts/AuthContext";
import ProtectedRoute from "../../components/ProtectedRoute";

// --- Authorization Config ---
import { TEAM_AUTH, TEAM_MENU_ITEMS } from "../../config/authorizationConfig";

// --- Page Components ---
import ManageTeamLeave from "./ManageTeamLeave";
import TeamLogs from "./TeamLogs";
import TeamFallBack from "./TeamFallBack";
import TeamLeaveApply from "./TeamLeaveApply";
import MarkAttendance from "./MarkAttendance";
import TimeSheet from "./TimeSheet";
import TeamReimbursements from "./TeamReimbursements";
import TeamLoanApproval from "./TeamLoanApproval";
import PayrollComp from "./PayrollComp";
import ManageAttendance from "./ManageAttendance";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import UpgradePlan from "../UpgradePlan";

// --- API ---
import axiosInstance from "../../axiosInstance";

// --- Styles ---
import "./Team.scss";

const Team = () => {
  const { isLoading, isAuthorized, canAccessModuleFeature, user } = useAuth();
  const [visibleSubmenu, setVisibleSubmenu] = useState(null);
  const [notificationCounts, setNotificationCounts] = useState({
    "Mark Attendance": 0,
    Dashboard: 0,
    "Manage Leave": 0,
    "Manage Attendance": 0,
    "Loan Approval": 0,
    Payroll: 0,
    Reimbursements: 0,
  });
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(true);
  const location = useLocation();

  // Enhanced authorization check that properly validates feature access
  const hasFeatureAccess = (authConfig) => {
    // If no auth config, it's public access
    if (!authConfig) {
      return true;
    }

    // Check roles first
    if (
      authConfig.roles &&
      !authConfig.roles.some((role) => user?.authorities?.includes(role))
    ) {
      return false;
    }

    // If feature is specified, check feature access
    if (authConfig.feature) {
      return canAccessModuleFeature(authConfig.feature);
    }

    // If only roles specified and user has required role, allow access
    return true;
  };

  // Enhanced menu items with access information
  const enhancedMenuItems = TEAM_MENU_ITEMS.map((item) => ({
    ...item,
    hasAccess: hasFeatureAccess(item.auth),
  }));

  // Filter menu items - show all for visual feedback
  // Note: Keeping this for backward compatibility if needed
  const filteredMenuItems = enhancedMenuItems;

  // Fetch notification counts
  useEffect(() => {
    const fetchNotificationCounts = async () => {
      if (!user) return;

      setIsLoadingNotifications(true);
      try {
        const updatedCounts = { ...notificationCounts };

        // Fetch notifications for managers
        if (isAuthorized({ roles: ["ROLE_MANAGER"] })) {
          // Leave requests
          if (hasFeatureAccess(TEAM_AUTH.MANAGE_LEAVE)) {
            try {
              const leaveResponse = await axiosInstance.get(
                `${process.env.REACT_APP_API_MANAGER_URL}/leave-history/pending`
              );
              updatedCounts["Manage Leave"] =
                leaveResponse.data.data?.length || 0;
            } catch (error) {
              console.error("Error fetching leave requests:", error);
              updatedCounts["Manage Leave"] = 0;
            }
          }

          // Attendance punchout requests
          if (hasFeatureAccess(TEAM_AUTH.MANAGE_ATTENDANCE)) {
            try {
              const attendanceResponse = await axiosInstance.get(
                `${process.env.REACT_APP_API_MANAGER_URL}/attendance/punchout-requests`
              );
              updatedCounts["Manage Attendance"] =
                attendanceResponse.data.data?.length || 0;
            } catch (error) {
              console.error("Error fetching attendance requests:", error);
              updatedCounts["Manage Attendance"] = 0;
            }
          }

          // Inside fetchNotificationCounts
          if (isAuthorized({ roles: ["ROLE_MANAGER"] })) {
            // Timesheets
            if (hasFeatureAccess(TEAM_AUTH.TIME_SHEET)) {
              try {
                const timesheetResponse = await axiosInstance.get(
                  `${process.env.REACT_APP_API_MANAGER_URL}/timesheets/pending`
                );
                updatedCounts["Team Time Sheet"] =
                  timesheetResponse.data.data?.length || 0;
              } catch (error) {
                console.error("Error fetching pending timesheets:", error);
                updatedCounts["Team Time Sheet"] = 0;
              }
            }
          }

          // Reimbursement requests
          if (hasFeatureAccess(TEAM_AUTH.REIMBURSEMENTS)) {
            try {
              const reimbursementResponse = await axiosInstance.get(
                `${process.env.REACT_APP_API_MANAGER_URL}/reimbursements/requested`
              );
              const requestedReimbursements =
                reimbursementResponse.data.data?.filter(
                  (reimb) => reimb.managerApprovalStatus === "Requested"
                ) || [];
              updatedCounts["Reimbursements"] = requestedReimbursements.length;
            } catch (error) {
              console.error("Error fetching reimbursement requests:", error);
              updatedCounts["Reimbursements"] = 0;
            }
          }
        }

        // Fetch notifications for HR
        if (isAuthorized({ roles: ["ROLE_HR"] })) {
          // Emergency fund requests
          if (hasFeatureAccess(TEAM_AUTH.LOAN_APPROVAL)) {
            try {
              const loanResponse = await axiosInstance.get(
                `${process.env.REACT_APP_API_HR_URL}/emergency-fund/requested`
              );
              const requestedLoans =
                loanResponse.data.data?.filter(
                  (fund) => fund.fundApprovalStatus === "Requested"
                ) || [];
              updatedCounts["Loan Approval"] = requestedLoans.length;
            } catch (error) {
              console.error("Error fetching loan requests:", error);
              updatedCounts["Loan Approval"] = 0;
            }
          }
        }

        setNotificationCounts(updatedCounts);
      } catch (error) {
        console.error("Error fetching notification data:", error);
      } finally {
        setIsLoadingNotifications(false);
      }
    };

    fetchNotificationCounts();

    // Set up interval to refresh notification counts (every 5 minutes)
    const refreshInterval = setInterval(fetchNotificationCounts, 300000);
    return () => clearInterval(refreshInterval);
  }, [user, isAuthorized]);

  // Set active submenu based on current route
  useEffect(() => {
    const currentPath = location.pathname;
    const activeMenuIndex = filteredMenuItems.findIndex((item) =>
      item.routes.some((route) => route === currentPath)
    );
    if (activeMenuIndex !== -1) {
      const activeMenuItem = filteredMenuItems[activeMenuIndex];
      setVisibleSubmenu(activeMenuItem.subItems ? activeMenuIndex : null);
    }
  }, [location.pathname, filteredMenuItems]);

  const handleMenuClick = (index) => {
    const item = filteredMenuItems[index];
    if (item.subItems && item.subItems.length > 0) {
      setVisibleSubmenu(visibleSubmenu === index ? null : index);
    } else {
      setVisibleSubmenu(null);
    }
  };

  // Function to update notification counts after processing a request
  const updateNotificationCount = (menuName) => {
    setNotificationCounts((prevCounts) => ({
      ...prevCounts,
      [menuName]: Math.max(0, prevCounts[menuName] - 1),
    }));
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="team-container">
      <div className="custom-menu">
        {isLoadingNotifications ? (
          <div className="loading-indicator">
            <LoadingScreen />
          </div>
        ) : (
          filteredMenuItems.map((item, index) => {
            const notificationCount = item.notificationKey
              ? notificationCounts[item.notificationKey]
              : 0;
            const hasAccess = item.hasAccess;

            return (
              <div
                key={index}
                className={`custom-menu-item ${
                  visibleSubmenu === index ||
                  item.routes.includes(location.pathname)
                    ? "custom-active"
                    : ""
                } ${!hasAccess ? "custom-locked" : ""}`}
                onClick={() => handleMenuClick(index)}
                style={{
                  opacity: hasAccess ? 1 : 0.6,
                  position: "relative",
                }}
              >
                <Link to={item.routes[0]} className="custom-menu-link">
                  {item.name}

                  {/* Add lock icon for locked features */}
                  {!hasAccess && (
                    <Icon
                      icon="mdi:lock"
                      style={{
                        marginLeft: "8px",
                        fontSize: "14px",
                        color: "#f39c12",
                      }}
                    />
                  )}

                  {/* Add notification badge if count > 0 and has access */}
                  {hasAccess && notificationCount > 0 && (
                    <span
                      className="innermenu-notification-badge"
                      aria-label={`${notificationCount} notifications`}
                    >
                      {notificationCount}
                    </span>
                  )}
                </Link>
                {item.subItems && item.subItems.length > 0 && (
                  <span
                    className="arrow-icon"
                    style={{ transition: "transform 0.5s ease-in" }}
                  >
                    <Icon
                      icon="mdi:chevron-down"
                      style={{
                        transform:
                          visibleSubmenu === index
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                      }}
                    />
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>

      <div className="custom-content">
        <Routes>
          {/* Enhanced routes with upgrade plan fallback */}
          <Route
            path="/manage-team-leave"
            element={
              hasFeatureAccess(TEAM_AUTH.MANAGE_LEAVE) ? (
                <ManageTeamLeave
                  onRequestProcessed={() =>
                    updateNotificationCount("Manage Leave")
                  }
                />
              ) : (
                <UpgradePlan
                  featureName="Manage Team Leave"
                  description="Approve and manage leave requests from your team members"
                />
              )
            }
          />

          <Route
            path="/team-logs"
            element={
              hasFeatureAccess(TEAM_AUTH.DASHBOARD) ? (
                <TeamLogs />
              ) : (
                <UpgradePlan
                  featureName="Team Dashboard"
                  description="View team attendance logs and analytics"
                />
              )
            }
          />

          <Route
            path="/team-fallback"
            element={
              hasFeatureAccess(TEAM_AUTH.FALLBACK) ? (
                <TeamFallBack />
              ) : (
                <UpgradePlan
                  featureName="Team Fallback"
                  description="Manage attendance fallback settings for your team"
                />
              )
            }
          />

          <Route
            path="/team-leave-apply"
            element={
              hasFeatureAccess(TEAM_AUTH.APPLY_LEAVE) ? (
                <TeamLeaveApply />
              ) : (
                <UpgradePlan
                  featureName="Apply Leave for Team"
                  description="Apply leave on behalf of your team members"
                />
              )
            }
          />

          <Route
            path="/manage-attendance"
            element={
              hasFeatureAccess(TEAM_AUTH.MANAGE_ATTENDANCE) ? (
                <ManageAttendance
                  onRequestProcessed={() =>
                    updateNotificationCount("Manage Attendance")
                  }
                />
              ) : (
                <UpgradePlan
                  featureName="Manage Team Attendance"
                  description="Approve attendance corrections and manage team attendance"
                />
              )
            }
          />

          <Route
            path="/mark-attendance"
            element={
              hasFeatureAccess(TEAM_AUTH.MARK_ATTENDANCE) ? (
                <MarkAttendance
                  onRequestProcessed={() =>
                    updateNotificationCount("Mark Attendance")
                  }
                />
              ) : (
                <UpgradePlan
                  featureName="Mark Team Attendance"
                  description="Mark attendance for your team members"
                />
              )
            }
          />

          <Route
            path="/team-time-sheet"
            element={
              hasFeatureAccess(TEAM_AUTH.TIME_SHEET) ? (
                <TimeSheet />
              ) : (
                <UpgradePlan
                  featureName="Team Time Sheet"
                  description="Manage and review team member timesheets"
                />
              )
            }
          />

          <Route
            path="/team-reimbursements"
            element={
              hasFeatureAccess(TEAM_AUTH.REIMBURSEMENTS) ? (
                <TeamReimbursements
                  onRequestProcessed={() =>
                    updateNotificationCount("Reimbursements")
                  }
                />
              ) : (
                <UpgradePlan
                  featureName="Team Reimbursements"
                  description="Approve and manage reimbursement requests from team members"
                />
              )
            }
          />

          <Route
            path="/team-loan-approval"
            element={
              hasFeatureAccess(TEAM_AUTH.LOAN_APPROVAL) ? (
                <TeamLoanApproval
                  onRequestProcessed={() =>
                    updateNotificationCount("Loan Approval")
                  }
                />
              ) : (
                <UpgradePlan
                  featureName="Loan Approval"
                  description="Review and approve emergency fund requests"
                />
              )
            }
          />

          <Route
            path="/comp-payroll"
            element={
              hasFeatureAccess(TEAM_AUTH.PAYROLL) ? (
                <PayrollComp
                  onRequestProcessed={() => updateNotificationCount("Payroll")}
                />
              ) : (
                <UpgradePlan
                  featureName="Payroll Management"
                  description="Manage payroll operations and employee compensation"
                />
              )
            }
          />

          <Route
            path="/"
            element={
              <Navigate
                to={
                  hasFeatureAccess(TEAM_AUTH.DASHBOARD)
                    ? "/team/team-logs"
                    : hasFeatureAccess(TEAM_AUTH.MANAGE_LEAVE)
                    ? "/team/manage-team-leave"
                    : "/team/team-logs" // Will show upgrade plan
                }
                replace
              />
            }
          />
        </Routes>
      </div>
    </div>
  );
};

export default Team;
